import { useState, useCallback, memo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trackComponentRender } from '@/lib/performance-monitor';
import { RouteErrorBoundary } from "@/components/error/RouteErrorBoundary";
import { DashboardGrid } from "@/components/dashboard/DashboardGrid";
import SuccessionInsights from "./SuccessionInsights";
import { MySuccessionScoreOverlay } from "@/components/dashboard/MySuccessionScoreOverlay";
import { DocumentSelectionDialog } from "@/components/dashboard/DocumentSelectionDialog";
import { UploadDocumentDialog } from "@/components/dashboard/UploadDocumentDialog";
import { AssessmentOverlay } from "@/components/assessment/AssessmentOverlay";
import { TimelineDateOverlay } from "@/components/dashboard/TimelineDateOverlay";
import { AssessmentProvider } from "@/context/AssessmentContext";
import { useDashboardAssessments } from "@/hooks/useDashboardAssessments";
import { useAuth } from "@/contexts/AuthContext";
import type { AssessmentModule } from "@/types/assessment";

const Dashboard = memo(function Dashboard() {
  const renderStart = performance.now();
  const [showInsights, setShowInsights] = useState(false);
  const [showScoreOverlay, setShowScoreOverlay] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState<AssessmentModule | null>(null);
  const [showTimelineEdit, setShowTimelineEdit] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { firstIncompleteModule, getNextIncompleteModule } = useDashboardAssessments();

  // Open score overlay if navigated with ?overlay=score
  useEffect(() => {
    if (searchParams.get('overlay') === 'score') {
      setShowScoreOverlay(true);
      searchParams.delete('overlay');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);
  
  // Track dashboard render performance
  useEffect(() => {
    trackComponentRender('Dashboard', renderStart);
  }, [renderStart]);
  
  const handleDocumentSelect = useCallback((documentId: string) => {
    navigate(`/documents?selected=${documentId}`);
  }, [navigate]);
  
  const handleCloseInsights = useCallback(() => {
    setShowInsights(false);
  }, []);
  
  const handleCloseScoreOverlay = useCallback(() => {
    setShowScoreOverlay(false);
  }, []);

  const handleOpenScoreOverlay = useCallback(() => {
    setShowScoreOverlay(true);
  }, []);

  const handleOpenInsights = useCallback(() => {
    setShowInsights(true);
  }, []);

  const handleModuleSelect = useCallback((moduleId: string, title: string) => {
    setSelectedModule({ id: moduleId, title, description: "", status: "not-started", estimatedTime: "", sections: [] });
  }, []);

  /** Invalidate all dashboard-related queries after an assessment is completed */
  const handleAssessmentCompleted = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["dashboard-assessment-modules"] });
    queryClient.invalidateQueries({ queryKey: ["userScores"] });
    queryClient.invalidateQueries({ queryKey: ["userMcfValues"] });
  }, [queryClient]);

  /** Open the next incomplete assessment overlay */
  const handleNextAssessment = useCallback((completedModuleId?: string) => {
    const TITLES: Record<string, string> = {
      "owner-readiness": "mySuccession Snapshot",
      "owner-readiness-exit-timeline": "Readiness & Exit Timeline",
      "business-profile": "Business Profile",
      "business-valuation": "Business Valuation",
      "business-optimization": "Business Optimization Review",
      "advisory-team": "Advisory Team",
      "financial-readiness": "Financial Readiness",
      "successor-strategy": "mySuccession Strategy",
      "governance-family": "Governance & Family",
      "exit-structure": "Exit Structure & Tax Optimization",
      "mysuccession-plan": "mySuccession Plan Assembly",
    };

    // Use exclusion-aware lookup when we know which module just completed
    const nextId = completedModuleId
      ? getNextIncompleteModule(completedModuleId)
      : firstIncompleteModule;

    if (nextId) {
      const title = TITLES[nextId] || nextId;
      handleModuleSelect(nextId, title);
    }
  }, [firstIncompleteModule, getNextIncompleteModule, handleModuleSelect]);
  
  return (
    <RouteErrorBoundary routeName="dashboard">
      <div className="space-y-6">
        
        <DashboardGrid 
          onScoreClick={handleOpenScoreOverlay}
          onInsightsClick={handleOpenInsights}
          onModuleSelect={handleModuleSelect}
          onTimelineEdit={() => setShowTimelineEdit(true)}
        />

        {/* Succession Insights Pop-up */}
        {showInsights && <SuccessionInsights onClose={handleCloseInsights} />}

        {/* mySuccession Score Overlay */}
        {showScoreOverlay && <MySuccessionScoreOverlay onClose={handleCloseScoreOverlay} />}

        {/* Assessment Overlay */}
        <AssessmentProvider>
          <AssessmentOverlay
            module={selectedModule}
            onOpenChange={(open) => !open && setSelectedModule(null)}
            onNextAssessment={handleNextAssessment}
            onCompleted={handleAssessmentCompleted}
          />
        </AssessmentProvider>

        {/* Timeline Date Edit Overlay */}
        <TimelineDateOverlay open={showTimelineEdit} onClose={() => setShowTimelineEdit(false)} />

        {/* Document Selection Dialog */}
        <DocumentSelectionDialog 
          open={showDocumentDialog} 
          onOpenChange={setShowDocumentDialog} 
          onSelectDocument={handleDocumentSelect} 
        />

        {/* Upload Document Dialog */}
        <UploadDocumentDialog 
          open={showUploadDialog} 
          onOpenChange={setShowUploadDialog} 
        />
      </div>
    </RouteErrorBoundary>
  );
});

Dashboard.displayName = "Dashboard";

export default Dashboard;
